import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Button, Icon } from '@aqarmap/aqarmap-ui';
import LayoutSkeleton from '~/components/LayoutSkeleton';
import { Languages } from '~/constants/enums';
import {
  useGetUserPhonesQuery,
  useRemovePhoneNumberMutation,
} from '~/redux/api/userApiSlice';
import AddContactModal from '~/modules/myAccount/pages/ManageContact/AddContactModal';
import ContactItem from '~/modules/myAccount/pages/ManageContact/ContactItem';
import Modal from '~/components/Modal';
import { useAppDispatch } from '~/redux/hooks';
import { closeModal, openModal } from '~/redux/features/uiState/uiStateSlice';
import PageLayout from '~myAccount/components/PageLayout';

const ManageContact = () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation('app');
  const [showAllPhones, setShowAllPhones] = useState(false);
  const [deletedNumber, setDeletedNumber] = useState<number | null>(null);
  const {
    data: userPhones,
    isLoading: isUserPhoneLoading,
    isSuccess: isUserPhoneSuccess,
  } = useGetUserPhonesQuery();
  const [
    removePhoneNumber,
    { isLoading: isRemoveLoading, isSuccess: removePhoneIsSuccess },
  ] = useRemovePhoneNumberMutation();

  const locale = i18n.language;

  useEffect(() => {
    if (removePhoneIsSuccess) {
      dispatch(closeModal('deleteNumber'));
    }
  }, [removePhoneIsSuccess]);

  return (
    <PageLayout
      bodyClassName="border border-gray rounded-[8px]"
      title={t('manageContact')}
      subTitle={t('manageContactDescription')}
      ctas={
        <div className="flex items-start justify-between">
          <div className="group relative update-phone">
            {!isUserPhoneLoading && (
              <div className="flex items-center gap-2x">
                {Number(userPhones?.length) >= 2 && (
                  <Icon name="info" className="text-blue" />
                )}
                <Button
                  size="medium"
                  ariaLabel="add-btn"
                  onClick={() => dispatch(openModal('addPhoneNumber'))}
                  variant="primary"
                  disabled={Number(userPhones?.length) >= 2}
                  className="h-10 w-[66px] disabled:hover:bg-blue_light_1"
                >
                  {t('add')}
                </Button>
                <AddContactModal />
              </div>
            )}

            {Number(userPhones?.length) >= 2 && (
                <Icon name="info" className="text-blue" />
              ) && (
                <div
                  className={`bg-gray__dark_2 flex invisible opacity-0 group-hover:opacity-[1] group-hover:visible transition-all duration-200 ease-in-out font-[300] absolute ${locale === Languages.ARABIC ? 'left-0' : 'right-0'} top-[48px] rounded-[6px] text-white w-[242px] py-x px-1.5x`}
                >
                  <div
                    className={`border-[10px] top-[-19px] ${locale === Languages.ARABIC ? 'left-[calc(20px+66px)]' : 'right-[calc(20px+66px)]'} -z-10 absolute border-l-transparent border-r-transparent border-t-transparent border-b-gray__dark_2`}
                  ></div>

                  <p>{t('manageContactNote')}</p>
                </div>
              )}
          </div>
        </div>
      }
    >
      <>
        <div className="flex flex-col  py-3x">
          <div className="border-b border-gray px-3x pb-1.5x">
            <label className="text-title_4 block">
              {t('advertisementNumbers')}
            </label>
          </div>
          {isUserPhoneLoading &&
            Array(2)
              .fill(0)
              .map((_, i) => (
                <div key={i} className="pt-3x px-3x">
                  <LayoutSkeleton />
                </div>
              ))}
          {!isUserPhoneLoading && isUserPhoneSuccess && (
            <>
              <ul className="flex flex-col">
                {userPhones
                  .slice(0, 3)
                  .reverse()
                  .map((item) => (
                    <ContactItem
                      key={item.id}
                      item={item}
                      disabled={userPhones?.length === 1}
                      onDeleteItem={(id: number) => setDeletedNumber(id)}
                    />
                  ))}
                {showAllPhones &&
                  userPhones
                    .slice(3)
                    .reverse()
                    .map((item) => (
                      <ContactItem
                        key={item.id}
                        item={item}
                        disabled={userPhones?.length === 1}
                        onDeleteItem={(id: number) => setDeletedNumber(id)}
                      />
                    ))}
              </ul>
              {userPhones.length > 2 && (
                <button
                  type="button"
                  aria-label="add-more"
                  onClick={() => setShowAllPhones(!showAllPhones)}
                  className="w-fit mt-2x px-3x"
                >
                  <span className=" text-link text-blue font-[700]">
                    {showAllPhones
                      ? t('showLessNumbers')
                      : t('showMoreNumbers')}
                  </span>
                </button>
              )}
            </>
          )}
        </div>

        <Modal
          modalKey="deleteNumber"
          title={t('advertisementNumbers')}
          Footer={
            <Button
              size="medium"
              onClick={() => removePhoneNumber(deletedNumber as number)}
              ariaLabel="delete-btn"
              disabled={isRemoveLoading}
              variant="secondary"
              className="w-full h-10 text-red_dark font-[700]"
            >
              {t('delete')}
              {isRemoveLoading && (
                <span className="loading loading-spinner loading-sm"></span>
              )}
            </Button>
          }
        >
          <p className="text-body_1 text-black text-lg ">{t('deleteNumber')}</p>
        </Modal>
      </>
    </PageLayout>
  );
};

export default ManageContact;
